"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RefreshCw, Shield, Pencil, Save, Trash2, MailCheck } from "lucide-react";

export function AdminDashboard() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGlobal2FA, setIsGlobal2FA] = useState(true); // ברירת מחדל
  const API_BASE_URL = "https://api.tuviai.org";

  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") return null;
    return token;
  }, []);

  const fetch2FAStatus = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/global-2fa-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsGlobal2FA(data.is_2fa_enabled);
      }
    } catch (e) {
      console.error("שגיאה במשיכת סטטוס 2FA");
    }
  }, [getAuthToken]);

  const toggleGlobal2FA = async () => {
    const actionText = isGlobal2FA ? "לבטל" : "להפעיל";
    if (!window.confirm(`האם ברצונך ${actionText} אימות דו-שלבי (2FA) לכלל המשתמשים בארגון?`)) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/toggle-global-2fa`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsGlobal2FA(data.is_2fa_enabled); // עדכון הסטייט ב-UI!
      } else {
        alert("שגיאה בעדכון הגדרות 2FA.");
      }
    } catch (e) {
      console.error(e);
      alert("תקלת תקשורת מול השרת.");
    }
  };

  const fetchUsers = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error("שגיאה:", e.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchUsers();
    fetch2FAStatus();
  }, [fetchUsers, fetch2FAStatus]);

  const handleUpdate = async (userId: number, isApproved: boolean, role: string) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/update-user/${userId}?is_approved=${isApproved}&role=${role}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        // חיווי קטן למשתמש שהמייל נשלח אם האישור הצליח
        if (isApproved) {
            console.log("Approval mail triggered via Backend");
        }
        fetchUsers();
      }
    } catch (e) {
      console.error("עדכון נכשל", e);
    }
  };

  const handleDelete = async (userId: number, userName: string) => {
    const token = getAuthToken();
    if (!token) return;

    if (!window.confirm(`האם למחוק לצמיתות את המשתמש ${userName}?`)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/delete-user/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) fetchUsers();
    } catch (e) {
      console.error("מחיקה נכשלה:", e);
    }
  };

  const saveFullEdit = async () => {
    if (!editingUser) return;
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/update-profile/${editingUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          personal_id: editingUser.personal_id,
          email: editingUser.email
        })
      });

      if (res.ok) {
        setIsDialogOpen(false);
        fetchUsers();
      }
    } catch (e) {
      console.error("שמירה נכשלה", e);
    }
  };

  return (
    <div className="space-y-8 p-4 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header יוקרתי */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-[40px] shadow-2xl shadow-slate-200/40 border border-slate-100">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
             <Shield className="text-blue-900" size={28} />
             <h2 className="text-3xl font-black text-slate-900 tracking-tight">ניהול משתמשים</h2>
          </div>
          <p className="text-slate-500 font-bold pr-10">בקרה ואישור הרשאות</p>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <Button
            onClick={toggleGlobal2FA}
            className={`rounded-[20px] font-black h-14 px-6 shadow-sm transition-all border-2 ${
              isGlobal2FA
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
            }`}
          >
            <Shield className={`mr-2 ${isGlobal2FA ? "text-emerald-500" : "text-red-500"}`} size={20} />
            {isGlobal2FA ? "2FA גורף: מופעל" : "2FA גורף: מושבת"}
          </Button>
          <Button
            onClick={fetchUsers}
            variant="outline"
            disabled={loading}
            className="rounded-[20px] font-black h-14 px-8 gap-3 border-slate-200 hover:bg-blue-50 hover:text-blue-900 transition-all shadow-sm"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            {loading ? "סנכרון..." : "רענן רשימה"}
          </Button>
        </div>
      </div>

      {/* טבלה מקצועית ונגישה */}
      <div className="bg-white rounded-[40px] shadow-2xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
            <Table className="min-w-[900px] text-right" dir="rtl">
            <TableHeader className="bg-slate-50/80">
                <TableRow className="border-slate-100 hover:bg-transparent">
                <TableHead className="font-black text-slate-900 py-6 pr-10 text-right text-lg">משתמש</TableHead>
                <TableHead className="font-black text-slate-900 text-right text-lg">זיהוי וקשר</TableHead>
                <TableHead className="font-black text-slate-900 text-center w-[180px] text-lg">סטטוס אישור</TableHead>
                <TableHead className="font-black text-slate-900 text-right w-[160px] text-lg">דרגת הרשאה</TableHead>
                <TableHead className="font-black text-slate-900 pl-10 text-left text-lg">ניהול</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {users.length === 0 && !loading ? (
                <TableRow>
                    <TableCell colSpan={5} className="text-center py-20 text-slate-400 font-black text-xl italic">
                    לא נמצאו פניות ממתינות במערכת
                    </TableCell>
                </TableRow>
                ) : (
                users.map((u) => (
                    <TableRow key={u.id} className="border-slate-50 hover:bg-blue-50/30 transition-all duration-300">
                    <TableCell className="py-6 pr-10">
                        <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-blue-900 text-white flex items-center justify-center font-black text-lg shadow-lg">
                            {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <div>
                            <div className="font-black text-slate-900 text-xl">{u.first_name} {u.last_name}</div>
                            <div className="text-xs text-blue-600 font-black uppercase tracking-widest mt-0.5">@{u.username}</div>
                        </div>
                        </div>
                    </TableCell>
                    <TableCell>
                        <div className="space-y-1">
                        <div className="text-slate-700 font-bold text-base">מזהה: {u.personal_id}</div>
                        <div className="text-sm text-slate-400 font-medium">{u.email}</div>
                        </div>
                    </TableCell>

                    <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-2 bg-slate-50/50 p-3 rounded-2xl border border-slate-100/50">
                        <div dir="ltr">
                            <Switch
                            checked={u.is_approved}
                            onCheckedChange={(val) => handleUpdate(u.id, val, u.role)}
                            className="data-[state=checked]:bg-green-500 scale-110"
                            />
                        </div>
                        <div className="flex items-center gap-1.5">
                            {u.is_approved && <MailCheck size={12} className="text-green-600" />}
                            <span className={`text-[11px] font-black uppercase tracking-tighter ${
                                u.is_approved ? "text-green-600" : "text-amber-500 animate-pulse"
                            }`}>
                                {u.is_approved ? "גישה מאושרת" : "ממתין לאישור"}
                            </span>
                        </div>
                        </div>
                    </TableCell>

                    <TableCell>
                        <Select value={u.role} onValueChange={(val) => handleUpdate(u.id, u.is_approved, val)}>
                        <SelectTrigger className="w-full h-12 rounded-xl bg-white border-2 border-slate-100 font-black text-slate-700 focus:ring-blue-900 transition-all">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl font-bold">
                            <SelectItem value="Standard">Standard</SelectItem>
                            <SelectItem value="Manager">Manager</SelectItem>
                            <SelectItem value="Admin">Admin</SelectItem>
                        </SelectContent>
                        </Select>
                    </TableCell>
                    <TableCell className="pl-10">
                        <div className="flex items-center gap-2 justify-end">
                        <Button size="icon" variant="ghost" className="h-12 w-12 rounded-2xl text-blue-900 hover:bg-blue-100 transition-colors" onClick={() => { setEditingUser(u); setIsDialogOpen(true); }}>
                            <Pencil size={20} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-12 w-12 rounded-2xl text-red-500 hover:bg-red-50 transition-colors" onClick={() => handleDelete(u.id, `${u.first_name} ${u.last_name}`)}>
                            <Trash2 size={20} />
                        </Button>
                        </div>
                    </TableCell>
                    </TableRow>
                ))
                )}
            </TableBody>
            </Table>
        </div>
      </div>

      {/* Dialog עריכה משופר */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-[48px] p-10 border-none shadow-3xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black text-right text-slate-900">עדכון פרטי קצין</DialogTitle>
            <DialogDescription className="text-right font-bold text-slate-400 mt-2">
              ערוך את פרטי המשתמש במאגר הנתונים המרכזי.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-8">
            {[
              { id: "first_name", label: "שם פרטי" },
              { id: "last_name", label: "שם משפחה" },
              { id: "personal_id", label: "מספר אישי (מ\"א)" },
              { id: "email", label: "כתובת דוא\"ל רשמית" }
            ].map((field) => (
              <div key={field.id} className="space-y-2">
                <Label htmlFor={field.id} className="font-black text-slate-700 text-base pr-1">{field.label}</Label>
                <Input
                  id={field.id}
                  value={editingUser?.[field.id] || ""}
                  onChange={(e) => setEditingUser({...editingUser, [field.id]: e.target.value})}
                  className="h-14 rounded-2xl border-2 border-slate-100 font-bold text-lg focus-visible:ring-blue-900 px-6"
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-4 mt-4">
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)} className="rounded-2xl font-bold h-14 flex-1">ביטול</Button>
            <Button onClick={saveFullEdit} className="bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-black h-14 px-10 gap-3 flex-2 shadow-xl">
              <Save size={20} /> שמור נתונים
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}